// `(unroll ds :col :limit N :index-name :idx)` — array column → multiple
// rows. Top-level eager op; composes via threading.

import { z } from 'zod';

import { astShape, nodePos } from '../../normalize.ts';
import { type ColumnRefInArg, opMeta, type PropagateSchemaCtx, type SpecialCtx } from '../../op-meta.ts';
import { paramPath } from '../../path.ts';
import { materializedToRows } from '../../runtime/arquero.ts';
import { astArg } from '../../schema/ast-codec.ts';
import { type ColumnName, ColumnNameSchema, columnNameString, KeywordSchema } from '../../schema/keyword.ts';
import { type ColumnType, type Schema, schemaFromEntries, unknownCol } from '../../schema/types.ts';
import { type Cell, MaterializedDataset, type Value } from '../../value.ts';
import {
  columnNameAstName,
  DEFAULT_RESHAPE_LIMIT,
  LimitSchema,
  requireColumns,
  resolveDsArg,
  rowsToColumns,
} from './helpers.ts';

/**
 * `(unroll ds :col :limit N :index-name :idx)` — explode an array column
 * into multiple rows. Each element of the source array becomes its own
 * row carrying the original id columns.
 *
 *   - `:col` (positional, keyword | string, required) — the source array
 *     column. The output column of the same name carries the per-element
 *     value.
 *   - `:limit` (int, optional) — caps the per-row fan-out (default
 *     `DEFAULT_RESHAPE_LIMIT`).
 *   - `:index-name` (keyword, optional) — when set, adds a 0-based
 *     index column with this name.
 *
 * Rows whose `:col` cell isn't an array are dropped — they contribute
 * zero rows to the output.
 *
 * Errors:
 *
 *   - First arg not a `Dataset` → `TypeMismatchError`.
 *   - `:col` isn't a column on `ds.schema` → `UnknownColumnRefError`
 *     (with the dataset's columns as the "did you mean").
 *
 * A `LazyDataset` input is forced via `ctx.materialize` before the
 * reshape runs.
 */
export const unroll = z
  .object({
    name: z.literal('unroll'),
    params: z.tuple([astArg('<dataset>'), ColumnNameSchema]),
    limit: LimitSchema,
    'index-name': KeywordSchema.optional(),
  })
  .describe(
    'Array column → multiple rows. Positional `:col` is the source array column; ' +
      '`:limit` caps per-row fan-out; `:index-name` adds a 0-based index column.',
  )
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(-> wells (unroll :tags :index-name :pos))' },
    apply: async (data, ctx: SpecialCtx): Promise<MaterializedDataset> => {
      const [dsRaw, colRaw] = data.params as readonly [Value, ColumnName];
      const concrete = await resolveDsArg(dsRaw, '(unroll ds :col)', ctx);
      const sourceCol = columnNameString(colRaw);
      const limit = data.limit ?? DEFAULT_RESHAPE_LIMIT;
      const indexName = data['index-name']?.name;

      requireColumns([sourceCol], concrete.schema, () => nodePos(ctx.spans, paramPath(ctx.path, 1)));

      const rows = materializedToRows(concrete);
      const otherCols = Array.from(concrete.schema.keys()).filter((c) => c !== sourceCol);

      // Element-count per source row drives BOTH the output rows and the
      // replicated identity axis, so they stay row-aligned by construction.
      // A non-array cell contributes 0 rows (and 0 identities).
      const elementCount = (row: Record<string, unknown>): number => {
        const v = row[sourceCol];
        return Array.isArray(v) ? v.slice(0, limit).length : 0;
      };

      const outRows = rows.flatMap((row) => {
        const v = row[sourceCol];
        if (!Array.isArray(v)) {
          return [];
        }
        return v.slice(0, limit).map((el, i) => ({
          ...Object.fromEntries(otherCols.map((c) => [c, row[c] as Cell])),
          [sourceCol]: el as Cell,
          ...(indexName === undefined ? {} : { [indexName]: i as Cell }),
        }));
      });

      const finalCols = indexName === undefined ? [...otherCols, sourceCol] : [...otherCols, sourceCol, indexName];
      const outCols = rowsToColumns(outRows, finalCols);
      const entries: (readonly [string, ColumnType])[] = finalCols.map((c) => {
        if (c === sourceCol || c === indexName) {
          return [c, unknownCol] as const;
        }
        return [c, concrete.schema.get(c) ?? unknownCol] as const;
      });
      // 1:N — each element row inherits its source row's identity; a source
      // row with no array elements contributes none (same as its 0 output rows).
      const axis = concrete.identity;
      const identity =
        axis === undefined
          ? undefined
          : rows.flatMap((row, i) => Array.from({ length: elementCount(row) }, () => axis[i]));
      return new MaterializedDataset(outCols, schemaFromEntries(entries), identity);
    },
    dataset: {
      // Output schema: same column list as input (source col stays,
      // with its array element type), plus an optional `:index-name`
      // column. When the source col's type isn't `arrayCol`, fall back
      // to `unknown` for the scalarised entry — apply's runtime path
      // drops non-array rows but the schema still names the column.
      propagateSchema: (data, ctx: PropagateSchemaCtx): Schema | undefined => {
        const params = (data as { readonly params?: readonly unknown[] }).params ?? [];
        const sourceCol = columnNameAstName(params[1]);
        if (sourceCol === undefined) {
          return undefined;
        }
        const indexShape = astShape((data as { readonly 'index-name'?: unknown })['index-name']);
        const indexName = indexShape.kind === 'keyword' ? indexShape.name : undefined;
        const elementType = ((): ColumnType => {
          const t = ctx.inputSchema.get(sourceCol);
          return t !== undefined && t.kind === 'array' ? t.element : unknownCol;
        })();
        const entries: (readonly [string, ColumnType])[] = Array.from(ctx.inputSchema.entries(), ([k, v]) =>
          k === sourceCol ? ([k, elementType] as const) : ([k, v] as const),
        );
        return schemaFromEntries(indexName === undefined ? entries : [...entries, [indexName, unknownCol]]);
      },
      // Positional `:col` is the only column USAGE — `:index-name` is
      // an output definition, excluded.
      referencedColumns: (data, basePath): readonly ColumnRefInArg[] => {
        const params = (data as { readonly params?: readonly unknown[] }).params ?? [];
        const name = columnNameAstName(params[1]);
        return name === undefined ? [] : [{ name, path: paramPath(basePath, 1) }];
      },
    },
  });
